import { IconArrowRight } from "central-icons/IconArrowRight";
import { useSetAtom } from "jotai";
import type { ReactNode } from "react";
import { useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { Action, ActionGroup } from "@/actions/action-types";
import { useRegisterCommandPaletteActions } from "@/actions/command-palette-registry";
import { useEnvironmentActions } from "@/actions/use-environment-actions";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { revealServicesAtom, renameEnvironmentAtom } from "@/atoms/ui-state";
import { DeleteAgentExecutionModal } from "@/components/ai/DeleteAgentExecutionModal";
import { SYSTEM_PORT_NAMES } from "@/components/command-palette/constants";
import { EnvironmentContextBadge } from "@/components/command-palette/EnvironmentContextBadge";
import { useToast } from "@/components/flexkit/toast";
import { useSessionCommandPaletteActions } from "@/hooks/use-session-command-palette-actions";
import { serviceToggleAction, useToggleService } from "@/hooks/use-toggle-service";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { isManualTask, useListServices, useListTasks, useStartTask } from "@/queries/automations-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { DeleteEnvironmentModal, ForceDeleteEnvironmentModal } from "@/routes/environments/DeleteEnvironmentModal";
import { NAV_STATE_DETAILS } from "@/routes/environments/details/environment-nav-state";
import { canOpen } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";

/** Static mapping from action group key to display heading and sort priority. */
export const GROUP_HEADINGS: Record<string, { heading: string; priority: number }> = {
    lifecycle: { heading: "Environment", priority: 0 },
    navigate: { heading: "Environment", priority: 1 },
    edit: { heading: "Environment", priority: 2 },
    info: { heading: "Environment", priority: 3 },
    danger: { heading: "Environment", priority: 4 },
    open: { heading: "IDEs", priority: 50 },
    troubleshooting: { heading: "Troubleshooting", priority: 60 },
};

/**
 * Registers environment-specific command palette actions when mounted
 * on an environment details page. Cleans up on unmount.
 *
 * Returns a ReactNode containing delete confirmation modals that must
 * be rendered by the consuming component.
 */
export function useRegisterEnvironmentCommands(environmentId: string): ReactNode {
    const { data: environment } = useEnvironment(environmentId);
    const navigate = useNavigate();
    const { toast } = useToast();
    const setRevealServices = useSetAtom(revealServicesAtom);
    const setRenameRequested = useSetAtom(renameEnvironmentAtom(environmentId));

    const onRename = useCallback(() => {
        setRenameRequested(true);
    }, [setRenameRequested]);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
    const [resetSessionTarget, setResetSessionTarget] = useState<PlainAgentExecution | null>(null);

    const onDelete = useCallback(({ force }: { force: boolean }) => {
        if (force) {
            setShowForceDeleteModal(true);
        } else {
            setShowDeleteModal(true);
        }
    }, []);

    // Session-related actions from the shared hook
    const { sessionActions, troubleshootingActions } = useSessionCommandPaletteActions(environmentId, {
        onResetSession: setResetSessionTarget,
    });

    // Core environment actions from the unified hook
    const envActions = useEnvironmentActions(environmentId, { onRename, onDelete });

    // Ports — derived from spec + status URLs
    const portActions = useMemo((): Action[] => {
        if (!environment || !canOpen(environment)) return [];
        const specPorts = environment.spec?.ports ?? [];
        const statusPorts = environment.status?.environmentUrls?.ports ?? [];

        return specPorts
            .filter((spec) => !SYSTEM_PORT_NAMES.has(spec.name))
            .map((spec) => {
                const status = statusPorts.find((p) => p.port === spec.port);
                const url = status?.url;
                return {
                    id: `port-${spec.port}`,
                    label: spec.name ? `${spec.name} (:${spec.port})` : `Port ${spec.port}`,
                    icon: <IconArrowRight />,
                    keywords: ["port", "open", String(spec.port)],
                    onSelect: () => {
                        if (url) window.open(url, "_blank", "noopener,noreferrer");
                    },
                    disabled: !url,
                };
            });
    }, [environment]);

    // Services
    const { data: services } = useListServices(environmentId);
    const toggleService = useToggleService(environmentId);

    const serviceActions = useMemo((): Action[] => {
        if (!services || services.length === 0) return [];
        return services.map((service) => {
            const name = service.metadata?.name ?? service.id;
            const action = serviceToggleAction(service);

            if (action === "start" || action === "stop") {
                const shouldStart = action === "start";
                return {
                    id: `service-${service.id}`,
                    label: `${shouldStart ? "Start" : "Stop"} ${name}`,
                    icon: shouldStart ? <IconPlay size="sm" /> : <IconStop size="sm" />,
                    keywords: ["service", action, shouldStart ? "enable" : "disable", name],
                    onSelect: () => {
                        void toggleService(service, shouldStart);
                        setRevealServices((c) => c + 1);
                    },
                };
            }
            // Service is in a terminal/transitory state — navigate to logs
            return {
                id: `service-${service.id}`,
                label: name,
                icon: <IconArrowRight />,
                keywords: ["service"],
                onSelect: () => navigate(`/details/${environmentId}/services/${service.id}/logs`, NAV_STATE_DETAILS),
            };
        });
    }, [services, environmentId, navigate, toggleService, setRevealServices]);

    // Tasks
    const { data: tasks } = useListTasks(environmentId);
    const startTask = useStartTask();

    const handleStartTask = useCallback(
        async (taskId: string) => {
            try {
                await startTask.mutateAsync(taskId);
            } catch (error) {
                toast({ title: "Failed to start task", description: formatError(error) });
            }
        },
        [startTask, toast],
    );

    const taskActions = useMemo((): Action[] => {
        if (!tasks || tasks.length === 0) return [];
        return tasks.map((task) => {
            const manual = isManualTask(task);
            return {
                id: `task-${task.id}`,
                label: task.metadata?.name ?? task.id,
                icon: <IconArrowRight />,
                keywords: ["task", "run"],
                onSelect: manual
                    ? () => void handleStartTask(task.id)
                    : () => navigate(`/details/${environmentId}/task/${task.id}`, NAV_STATE_DETAILS),
            };
        });
    }, [tasks, environmentId, navigate, handleStartTask]);

    // Build groups — split envActions by their `group` field so IDEs
    // get their own section and navigate actions appear prominently.
    const groups = useMemo((): ActionGroup[] => {
        const result: ActionGroup[] = [];

        // Combine core env actions with session actions
        const allEnvActions = [...envActions, ...sessionActions];

        // Partition by group, merging groups that share a heading
        if (allEnvActions.length > 0) {
            const buckets = new Map<string, Action[]>();
            for (const action of allEnvActions) {
                const key = action.group ?? "default";
                if (!buckets.has(key)) buckets.set(key, []);
                buckets.get(key)!.push(action);
            }

            // Merge buckets that share the same heading into a single ActionGroup
            const merged = new Map<string, { heading: string; priority: number; actions: Action[] }>();
            for (const [key, actions] of buckets) {
                const meta = GROUP_HEADINGS[key] ?? { heading: "Environment", priority: 3 };
                const existing = merged.get(meta.heading);
                if (existing) {
                    existing.actions.push(...actions);
                    existing.priority = Math.min(existing.priority, meta.priority);
                } else {
                    merged.set(meta.heading, { heading: meta.heading, priority: meta.priority, actions: [...actions] });
                }
            }

            for (const [, entry] of merged) {
                result.push({
                    id: `env-${entry.heading.toLowerCase().replace(/\s+/g, "-")}`,
                    heading: entry.heading,
                    actions: entry.actions,
                    priority: entry.priority,
                });
            }
        }

        if (portActions.length > 0) {
            result.push({
                id: "env-ports",
                heading: "Ports",
                actions: portActions,
                priority: 10,
            });
        }

        if (serviceActions.length > 0) {
            result.push({
                id: "env-services",
                heading: "Services",
                actions: serviceActions,
                priority: 20,
            });
        }

        if (taskActions.length > 0) {
            result.push({
                id: "env-tasks",
                heading: "Tasks",
                actions: taskActions,
                priority: 30,
            });
        }

        if (troubleshootingActions.length > 0) {
            result.push({
                id: "env-troubleshooting",
                heading: "Troubleshooting",
                actions: troubleshootingActions,
                priority: 40,
            });
        }

        return result;
    }, [envActions, sessionActions, portActions, serviceActions, taskActions, troubleshootingActions]);

    const contextBadge = useMemo(
        () => (environmentId ? <EnvironmentContextBadge environmentId={environmentId} /> : undefined),
        [environmentId],
    );

    useRegisterCommandPaletteActions(`env-${environmentId}`, groups, { contextBadge });

    if (!environment) return null;

    return (
        <>
            <DeleteEnvironmentModal
                environment={environment}
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
            />
            <ForceDeleteEnvironmentModal
                environment={environment}
                open={showForceDeleteModal}
                onClose={() => setShowForceDeleteModal(false)}
            />
            {resetSessionTarget && (
                <DeleteAgentExecutionModal
                    agentExecution={resetSessionTarget}
                    open
                    onClose={() => setResetSessionTarget(null)}
                />
            )}
        </>
    );
}
